import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractTemplateApi } from '../../api/contracts';
import { ContractTemplate } from '../../types/contracts';
import { ApiError } from '../../api/client';
import { IconActionButton } from '../../components/IconActionButton';
import { Pencil, Power, PlusCircle } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

interface TemplateFormValues { name: string; category?: string; templateContent: string }

/** Real, selectable reference - every merge field VConnect actually populates, not a wall of text. Click Insert to add it to the template you're editing without needing to memorize or retype the exact placeholder syntax. */
const MERGE_FIELDS: { field: string; label: string }[] = [
  { field: 'staff_name', label: 'Employee Full Name' },
  { field: 'staff_id', label: 'Employee ID' },
  { field: 'position', label: 'Position Title' },
  { field: 'department', label: 'Department / Unit' },
  { field: 'duty_station', label: 'Duty Station' },
  { field: 'state', label: 'State' },
  { field: 'supervisor_name', label: 'Supervisor' },
  { field: 'employment_type', label: 'Employment Type' },
  { field: 'start_date', label: 'Contract Start Date' },
  { field: 'end_date', label: 'Contract End Date' },
  { field: 'probation_period', label: 'Probation Period' },
  { field: 'monthly_gross_salary', label: 'Monthly Gross Salary' },
  { field: 'grade', label: 'Salary Grade / Level' },
  { field: 'bank_name', label: 'Bank Name' },
  { field: 'bank_account_number', label: 'Bank Account Number' },
  { field: 'bank_account_name', label: 'Bank Account Name' },
  { field: 'phone', label: 'Phone Number' },
  { field: 'email', label: 'Email Address' },
  { field: 'residential_address', label: 'Residential Address' },
  { field: 'national_id', label: 'National ID (type and number)' },
  { field: 'emergency_contact', label: 'Emergency Contact (name, relationship, phone)' },
  { field: 'employee_signature_name', label: "Employee's typed signature (filled only when signed)" },
  { field: 'employee_signature_date', label: 'Date employee signed' },
  { field: 'employer_signature_name', label: "Organization's typed signature (filled only when finalised)" },
  { field: 'employer_signature_date', label: 'Date organization finalised' },
];

function MergeFieldReference({ getValues, setValue }: { getValues: UseFormGetValues<TemplateFormValues>; setValue: UseFormSetValue<TemplateFormValues> }) {
  const insert = (field: string) => {
    const current = getValues('templateContent') ?? '';
    setValue('templateContent', `${current}{{${field}}}`, { shouldDirty: true });
  };

  return (
    <div className="panel">
      <SectionHeader title="Available Merge Fields - click Insert to add to the template below" />
      <div className="panel-body" style={{ maxHeight: 260, overflowY: 'auto' }}>
        <DataTable
          rows={MERGE_FIELDS}
          rowKey={(f) => f.field}
          emptyMessage=""
          columns={[
            { header: 'Field', render: (f) => <code>{`{{${f.field}}}`}</code> },
            { header: 'What It Fills In', render: (f) => f.label },
            { header: '', render: (f) => <IconActionButton icon={PlusCircle} label="Insert" onClick={() => insert(f.field)} /> },
          ]}
        />
      </div>
    </div>
  );
}

export function ContractTemplatesPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ContractTemplate | null>(null);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const templatesQuery = useQuery({ queryKey: ['contract-templates'], queryFn: () => contractTemplateApi.list() });
  const { register, handleSubmit, reset, getValues, setValue } = useForm<TemplateFormValues>();
  const editForm = useForm<TemplateFormValues>({
    values: editing ? { name: editing.name, category: editing.category ?? undefined, templateContent: editing.template_content } : undefined,
  });

  const createMutation = useMutation({
    mutationFn: (values: TemplateFormValues) => contractTemplateApi.create(values),
    onSuccess: () => { showSuccess('Contract template created.'); queryClient.invalidateQueries({ queryKey: ['contract-templates'] }); setModalOpen(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create template.'),
  });

  const updateMutation = useMutation({
    mutationFn: (values: TemplateFormValues) => contractTemplateApi.update(editing!.id, values),
    onSuccess: () => { showSuccess('Template updated.'); queryClient.invalidateQueries({ queryKey: ['contract-templates'] }); setEditing(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update template.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => contractTemplateApi.deactivate(id),
    onSuccess: () => { showSuccess('Template deactivated.'); queryClient.invalidateQueries({ queryKey: ['contract-templates'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate template.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader
        title="Contract Templates"
        description="Merge fields are automatically replaced with real staff information when a contract is created - see the reference table inside each template form."
        actions={<button className="btn-primary" onClick={() => setModalOpen(true)}>New Template</button>}
      />

      {templatesQuery.data && (
        <div className="panel">
          <SectionHeader title="All Contract Templates" />
          <div className="panel-body">
        <DataTable
          rows={templatesQuery.data.data}
          rowKey={(t) => t.id}
          emptyMessage="No contract templates yet."
          columns={[
            { header: 'Name', render: (t) => t.name },
            { header: 'Category', render: (t) => t.category ?? '-' },
            { header: '', render: (t) => <span className="icon-action-group"><IconActionButton icon={Pencil} label="Edit" onClick={() => setEditing(t)} /> <IconActionButton icon={Power} label="Deactivate" onClick={() => deactivateMutation.mutate(t.id)} /></span> },
          ]}
        />
          </div>
        </div>
      )}

      {editing && (
        <Modal title={`Edit: ${editing.name}`} onClose={() => setEditing(null)}>
          <form onSubmit={editForm.handleSubmit((values) => updateMutation.mutate(values))}>
            <label>Name<input {...editForm.register('name', { required: true })} /></label>
            <label>Category<input {...editForm.register('category')} /></label>
            <MergeFieldReference getValues={editForm.getValues} setValue={editForm.setValue} />
            <label>Template Content<textarea rows={8} {...editForm.register('templateContent', { required: true })} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving…' : 'Save'}</button><button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button></div>
          </form>
        </Modal>
      )}

      {modalOpen && (
        <Modal title="New Contract Template" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>Name<input {...register('name', { required: true })} /></label>
            <label>Category<input {...register('category')} /></label>
            <MergeFieldReference getValues={getValues} setValue={setValue} />
            <label>Template Content<textarea rows={8} {...register('templateContent', { required: true })} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
